/**
 * Collaborative Session Page
 * Loads a collaborative puzzle session by sessionId from Realtime DB
 * Automatically loads puzzle, subscriptionId, and other details
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from '../../../../state/state';
import { selectActiveRole } from '../../../../state/UserStatesV2/userSliceV2';
import type { RootState } from '../../../../state/state';
import CollaborativePuzzleView from '../SubscriptionDetails/Components/CollaborativePuzzleView';
import { 
    getSessionById, 
    joinCollaborativeSession,
    CollaborativePuzzleSession 
} from '../../../../services/collaborativePuzzleService';

// Generate or retrieve guest user ID (fallback only - prefer Firebase anonymous UID)
const getGuestUserId = (): string => {
    const storageKey = 'collab_guest_user_id';
    let guestId = sessionStorage.getItem(storageKey);
    if (!guestId) {
        // Generate a unique ID for this guest session (fallback if anonymous sign-in fails)
        // Use slice instead of substr for better browser compatibility (IE, Firefox)
        guestId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(storageKey, guestId);
    }
    return guestId;
};

const CollaborativeSessionPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const activeRole = useAppSelector(selectActiveRole);
    const userData = useAppSelector((state: RootState) => state.userSliceV2.data);
    
    // Try to get logged in user, otherwise use guest
    const loggedInUserId = userData?.uid || '';
    const isAuthenticated = !!loggedInUserId;
    
    // Check for explicit role parameter in URL (e.g., ?role=teacher)
    const urlRole = searchParams.get('role') as 'teacher' | 'student' | null;
    
    // Check if role is cached in sessionStorage for this specific session
    const sessionRoleKey = `collab_session_${sessionId}_role`;
    const cachedSessionRole = sessionStorage.getItem(sessionRoleKey) as 'teacher' | 'student' | null;
    
    console.log('🔍 CollaborativeSessionPage Auth Check:', {
        sessionId,
        urlRole,
        cachedSessionRole,
        isAuthenticated,
        loggedInUserId,
        userData,
        activeRole: activeRole?.role,
        reduxState: userData
    });
    
    // Guest state
    const [guestName, setGuestName] = useState(() => {
        return sessionStorage.getItem('collab_guest_name') || '';
    });
    // Initialize dialog state based on whether name exists (for guests)
    // IMPORTANT: Don't show dialog if URL has role=teacher or cached role is teacher
    const [showNameDialog, setShowNameDialog] = useState(() => {
        // If URL or cached role is teacher, don't show name dialog
        if (urlRole === 'teacher' || cachedSessionRole === 'teacher') {
            return false;
        }
        // Only show dialog if not authenticated AND no name is stored
        const storedName = sessionStorage.getItem('collab_guest_name');
        return !loggedInUserId && (!storedName || !storedName.trim());
    });
    const [guestUserId, setGuestUserId] = useState<string>('');
    
    // Determine final user ID and name
    const finalUserId = isAuthenticated ? loggedInUserId : guestUserId;
    const finalUserName = isAuthenticated ? (activeRole?.displayName || 'User') : guestName;
    
    const [loading, setLoading] = useState(true);
    const [authLoading, setAuthLoading] = useState(true);
    const [anonymousAuthComplete, setAnonymousAuthComplete] = useState(false);
    const [session, setSession] = useState<CollaborativePuzzleSession | null>(null);
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [hasJoined, setHasJoined] = useState(false);

    // Wait for auth to load (on page refresh, Redux state loads async)
    useEffect(() => {
        // Wait until userData is loaded OR timeout after 1 second
        const checkAuth = () => {
            // If userData exists (user is logged in) OR we've waited long enough
            if (userData !== undefined || !authLoading) {
                setAuthLoading(false);
            }
        };
        
        // Check immediately
        checkAuth();
        
        // Also set a timeout as fallback
        const timer = setTimeout(() => {
            setAuthLoading(false);
        }, 500); // Give more time for auth to load
        
        return () => clearTimeout(timer);
    }, [userData, authLoading]);

    // Initialize guest user ID if not authenticated
    useEffect(() => {
        // IMPORTANT: Don't show name dialog if user has teacher role (via URL or cached)
        const isTeacher = urlRole === 'teacher' || cachedSessionRole === 'teacher';
        
        if (!isAuthenticated && !isTeacher) {
            // Sign in anonymously so guests can write to Realtime DB
            const signInAnonymouslyIfNeeded = async () => {
                try {
                    const { getAuth, signInAnonymously } = await import('firebase/auth');
                    const auth = getAuth();
                    
                    // Check if already signed in (including anonymous)
                    if (!auth.currentUser) {
                        console.log('🔓 Signing in guest anonymously...');
                        const userCredential = await signInAnonymously(auth);
                        const anonymousUser = userCredential.user;
                        console.log('✅ Guest signed in anonymously, UID:', anonymousUser.uid);
                        // Use the Firebase anonymous user's UID instead of generated guest ID
                        setGuestUserId(anonymousUser.uid);
                        // Store the anonymous UID in sessionStorage for persistence
                        sessionStorage.setItem('collab_guest_user_id', anonymousUser.uid);
                        setAnonymousAuthComplete(true);
                    } else if (auth.currentUser.isAnonymous) {
                        // Already signed in anonymously, use their UID
                        console.log('✅ Already signed in anonymously, UID:', auth.currentUser.uid);
                        setGuestUserId(auth.currentUser.uid);
                        sessionStorage.setItem('collab_guest_user_id', auth.currentUser.uid);
                        setAnonymousAuthComplete(true);
                    } else {
                        // User is signed in but not anonymous - this shouldn't happen if !isAuthenticated
                        // But handle it anyway
                        console.log('⚠️ User is signed in but not anonymous, UID:', auth.currentUser.uid);
                        setGuestUserId(auth.currentUser.uid);
                        setAnonymousAuthComplete(true);
                    }
                } catch (error: any) {
                    console.error('❌ Error signing in anonymously:', error);
                    
                    // Check if it's the admin-restricted-operation error (anonymous auth disabled)
                    const isAuthRestricted = error.code === 'auth/admin-restricted-operation' || 
                                           (error.message && error.message.indexOf('admin-restricted-operation') !== -1);
                    
                    if (isAuthRestricted) {
                        console.warn('⚠️ Anonymous authentication is disabled in Firebase Console.');
                        console.warn('📝 To enable it: Firebase Console > Authentication > Sign-in method > Anonymous > Enable');
                        console.warn('🔄 Proceeding with guest ID - session may fail if RealtimeDB rules require authentication.');
                        
                        // Show user-friendly error message
                        toast.error('Anonymous authentication is disabled. Please contact administrator or enable it in Firebase Console.', {
                            autoClose: 5000
                        });
                    }
                    
                    // Fallback to generated guest ID if anonymous sign-in fails
                    const guestId = getGuestUserId();
                    setGuestUserId(guestId);
                    console.log('🔄 Using fallback guest ID:', guestId);
                    // Still mark as complete so we can try to proceed (RealtimeDB rules might allow unauthenticated reads)
                    setAnonymousAuthComplete(true);
                }
            };
            
            signInAnonymouslyIfNeeded();
            
            // Ensure name dialog state is correct - don't change it if already set correctly
            const storedName = sessionStorage.getItem('collab_guest_name');
            if (!storedName || !storedName.trim()) {
                // No name stored - ensure dialog is shown
                if (!showNameDialog) {
                    setShowNameDialog(true);
                }
            } else {
                // Name exists - ensure dialog is closed and name is set
                if (showNameDialog) {
                    setShowNameDialog(false);
                }
                if (guestName !== storedName) {
                    setGuestName(storedName);
                }
            }
        } else {
            // Authenticated OR teacher role - ensure dialog is closed
            if (showNameDialog) {
                setShowNameDialog(false);
            }
            // For authenticated users, mark auth as complete immediately
            setAnonymousAuthComplete(true);
        }
    }, [isAuthenticated, showNameDialog, guestName, urlRole, cachedSessionRole]);

    // Load session by sessionId and keep it subscribed for real-time updates
    // IMPORTANT: For guests, wait for anonymous auth to complete before loading session
    useEffect(() => {
        if (!sessionId) {
            setError('No session ID provided');
            setLoading(false);
            return;
        }

        // For guests, wait for anonymous authentication to complete before loading session
        // This ensures RealtimeDB permissions work correctly
        if (!isAuthenticated && !anonymousAuthComplete) {
            console.log('⏳ Waiting for anonymous authentication to complete before loading session...');
            return;
        }

        console.log('Loading session by ID:', sessionId, { isAuthenticated, anonymousAuthComplete });

        let sessionFound = false;
        const unsubscribe = getSessionById(sessionId, (loadedSession, foundSubscriptionId) => {
            if (loadedSession && foundSubscriptionId) {
                if (!sessionFound) {
                    sessionFound = true;
                    console.log('📦 Session loaded for first time:', {
                        sessionId,
                        subscriptionId: foundSubscriptionId,
                        participants: loadedSession.participants ? Object.entries(loadedSession.participants).map(([id, p]) => ({
                            id,
                            name: p.userName,
                            role: p.userRole,
                            isActive: p.isActive
                        })) : []
                    });
                    setLoading(false);
                } else {
                    console.log('📦 Session updated:', {
                        participantCount: loadedSession.participants ? Object.keys(loadedSession.participants).length : 0
                    });
                }
                
                // Check if session is active
                if (loadedSession.status !== 'active') {
                    setError('This session has ended');
                    setLoading(false);
                    return;
                }

                // Update session state (this will trigger real-time updates)
                setSession(loadedSession);
                setSubscriptionId(foundSubscriptionId);
            } else if (!sessionFound) {
                // Session not found yet, but callback was called - might be initial empty state
                // Don't show error immediately, wait for timeout
            }
        });

        // Timeout after 10 seconds (only for initial load)
        const timeout = setTimeout(() => {
            if (!sessionFound) {
                console.error('❌ Session not found after timeout:', {
                    sessionId,
                    isAuthenticated,
                    anonymousAuthComplete,
                    finalUserId
                });
                
                // Provide helpful error message based on auth state
                let errorMessage = 'Session not found or connection timeout.';
                if (!isAuthenticated) {
                    errorMessage += ' If anonymous authentication is disabled in Firebase, you may need to enable it in Firebase Console (Authentication > Sign-in method > Anonymous) or update RealtimeDB rules to allow unauthenticated reads.';
                }
                errorMessage += ' Please check that the session ID is correct and the session is still active.';
                
                setError(errorMessage);
                setLoading(false);
            }
        }, 10000);

        // Keep subscription active for real-time updates
        return () => {
            clearTimeout(timeout);
            unsubscribe();
        };
    }, [sessionId, isAuthenticated, anonymousAuthComplete]);

    // Handle guest name submission
    const handleGuestNameSubmit = () => {
        const trimmedName = guestName.trim();
        if (!trimmedName) {
            toast.error('Please enter your name');
            return;
        }
        // Save name to storage first
        sessionStorage.setItem('collab_guest_name', trimmedName);
        // Update state
        setGuestName(trimmedName);
        // Close dialog - this MUST happen last to prevent race conditions
        setShowNameDialog(false);
        console.log('✅ Name submitted:', trimmedName);
    };

    // Auto-join session once loaded - ONLY when name is set and dialog is closed
    useEffect(() => {
        // Don't join if name dialog is still showing
        if (showNameDialog) {
            return;
        }
        
        // For guests, ensure anonymous auth is complete before joining
        if (!isAuthenticated && !anonymousAuthComplete) {
            console.log('⏳ Waiting for anonymous authentication before joining session...');
            return;
        }
        
        // For guests, ensure name is set AND anonymous auth is complete (but skip this check for teachers with cached/URL role)
        if (!isAuthenticated && (!finalUserName || !finalUserName.trim()) && urlRole !== 'teacher' && cachedSessionRole !== 'teacher') {
            return;
        }
        
        // For guests, also ensure we have a user ID (anonymous sign-in completed)
        if (!isAuthenticated && !finalUserId) {
            return;
        }
        
        if (session && subscriptionId && !hasJoined && finalUserId && finalUserName) {
            // IMPORTANT: Determine role with this priority:
            // 1. Existing participant role in session (for page refreshes)
            // 2. SessionStorage cached role for this session (persists across refreshes)
            // 3. URL parameter (explicit role specification)
            // 4. Authentication and activeRole
            let userRole: 'teacher' | 'student' = 'student'; // Default to student
            
            // Get cached role for this specific session
            const sessionRoleKey = `collab_session_${sessionId}_role`;
            const cachedRole = sessionStorage.getItem(sessionRoleKey) as 'teacher' | 'student' | null;
            
            if (session.participants && session.participants[finalUserId]) {
                // User is already in the session - use their stored role (HIGHEST PRIORITY)
                userRole = session.participants[finalUserId].userRole;
                console.log('✅ Found existing participant, using their stored role:', userRole);
                // Cache this role for future refreshes
                sessionStorage.setItem(sessionRoleKey, userRole);
            } else if (cachedRole) {
                // Use cached role from previous visit to this session (SECOND PRIORITY)
                userRole = cachedRole;
                console.log('✅ Using cached role from sessionStorage:', userRole);
            } else if (urlRole) {
                // URL parameter specified role (for new participants)
                userRole = urlRole;
                console.log('🆕 New participant, using URL role:', userRole);
                // Cache this role
                sessionStorage.setItem(sessionRoleKey, userRole);
            } else {
                // New participant - determine role based on authentication and activeRole
                if (isAuthenticated) {
                    userRole = (activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin') ? 'teacher' : 'student';
                    console.log('🆕 New participant, determined role from activeRole:', userRole);
                    // Cache this role
                    sessionStorage.setItem(sessionRoleKey, userRole);
                } else {
                    userRole = 'student'; // Guests are always students
                    console.log('🆕 New guest participant, role: student');
                }
            }
            
            console.log('Auto-joining session...', { sessionId, subscriptionId, userId: finalUserId, userName: finalUserName, userRole });
            
            joinCollaborativeSession(
                sessionId!,
                subscriptionId,
                finalUserId,
                finalUserName,
                userRole
            )
            .then(() => {
                console.log('✅ Successfully joined session:', {
                    sessionId,
                    subscriptionId,
                    userId: finalUserId,
                    userName: finalUserName,
                    userRole
                });
                setHasJoined(true);
            })
            .catch((error) => {
                console.error('❌ Error joining session:', {
                    error: error.message,
                    sessionId,
                    subscriptionId,
                    userId: finalUserId,
                    userName: finalUserName,
                    userRole,
                    isAuthenticated,
                    anonymousAuthComplete
                });
                // Check if it's a "session full" error (use indexOf for IE compatibility)
                const errorMsg = error.message || '';
                if (errorMsg && errorMsg.indexOf('Session is full') !== -1) {
                    toast.error(error.message);
                    setError(error.message);
                    setLoading(false);
                } else if (errorMsg && errorMsg.indexOf('Session not found') !== -1) {
                    toast.error('Session not found. It may have been ended.');
                    setError('Session not found. It may have been ended.');
                    setLoading(false);
                } else {
                    // Silently handle if already a participant or other expected errors
                    console.log('Join session result (may be expected):', error.message);
                    setHasJoined(true); // Assume we're already in if join fails
                }
            });
        }
    }, [session, subscriptionId, hasJoined, finalUserId, finalUserName, activeRole, sessionId, isAuthenticated, showNameDialog, urlRole, anonymousAuthComplete]);

    // Show loading while auth state is loading (prevents flash of name dialog)
    // Only for guests who need to enter name
    if (authLoading && !urlRole && !cachedSessionRole) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    // If URL has ?role=teacher or cached role is teacher, trust it and let them in
    // Auth will be checked by Firebase Realtime DB rules anyway when writing data

    // Show name dialog for guests - BLOCK until name is entered
    // This check must be FIRST to prevent any other rendering
    if (!isAuthenticated && showNameDialog) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Dialog
                    open={true}
                    maxWidth="sm"
                    fullWidth
                    disableEscapeKeyDown={true}
                    onClose={(event, reason) => {
                        // Prevent closing by clicking outside or pressing escape
                        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                            return;
                        }
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            padding: '8px'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '20px 24px 16px',
                        borderBottom: '1px solid #e5e7eb'
                    }}>
                        <span style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
                            Join Collaborative Puzzle Session
                        </span>
                    </DialogTitle>
                    <DialogContent sx={{ padding: '24px' }}>
                        <div className="space-y-4">
                            <p className="text-gray-700" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                                Enter your name to join the collaborative puzzle solving session:
                            </p>
                            <TextField
                                fullWidth
                                label="Your Name"
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && guestName.trim()) {
                                        handleGuestNameSubmit();
                                    }
                                }}
                                placeholder="Enter your name"
                                autoFocus
                                variant="outlined"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                ⚠️ You must enter your name to continue
                            </p>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{ 
                        padding: '16px 24px 20px',
                        borderTop: '1px solid #e5e7eb',
                        gap: '12px'
                    }}>
                        <Button
                            onClick={handleGuestNameSubmit}
                            variant="contained"
                            disabled={!guestName.trim()}
                            sx={{
                                backgroundColor: '#9333ea',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#7e22ce'
                                },
                                '&:disabled': {
                                    backgroundColor: '#d1d5db',
                                    color: 'white'
                                }
                            }}
                        >
                            Join Session
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading collaborative session...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !session || !subscriptionId) {
        // Check if this is an authentication-related error
        const isAuthError = error && (
            error.indexOf('anonymous authentication') !== -1 || 
            error.indexOf('RealtimeDB rules') !== -1 ||
            error.indexOf('authentication') !== -1 ||
            error.indexOf('auth/admin-restricted-operation') !== -1
        );
        
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center max-w-2xl">
                    <div className="text-6xl mb-4">❌</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Session Not Found</h2>
                    <p className="text-gray-600 mb-4">{error || 'The collaborative session could not be loaded.'}</p>
                    
                    {isAuthError && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
                            <h3 className="font-bold text-yellow-800 mb-2">🔧 Configuration Required</h3>
                            <p className="text-sm text-yellow-700 mb-3">
                                Your RealtimeDB rules require authentication, but anonymous authentication is disabled. Choose one solution:
                            </p>
                            
                            <div className="mb-3">
                                <h4 className="font-semibold text-yellow-800 mb-1">Solution 1: Enable Anonymous Authentication (Recommended)</h4>
                                <ol className="text-sm text-yellow-700 list-decimal list-inside space-y-1 ml-2">
                                    <li>Go to <strong>Firebase Console</strong></li>
                                    <li>Navigate to <strong>Authentication → Sign-in method</strong></li>
                                    <li>Enable <strong>Anonymous</strong> sign-in provider</li>
                                    <li>Save the changes</li>
                                </ol>
                            </div>
                            
                            <div>
                                <h4 className="font-semibold text-yellow-800 mb-1">Solution 2: Update RealtimeDB Rules</h4>
                                <p className="text-xs text-yellow-700 mb-2">
                                    Update your RealtimeDB rules to allow unauthenticated access for collaborative sessions:
                                </p>
                                <pre className="bg-yellow-100 p-2 rounded text-xs overflow-x-auto">
{`{
  "rules": {
    "collaborativePuzzles": {
      ".read": true,
      ".write": true
    },
    ".read": "auth != null",
    ".write": "auth != null"
  }
}`}
                                </pre>
                                <p className="text-xs text-yellow-600 mt-2 italic">
                                    This allows public access to collaborative sessions while keeping other data secure.
                                </p>
                            </div>
                        </div>
                    )}
                    
                    {isAuthenticated ? (
                        <button
                            onClick={() => navigate('/mysubscriptions')}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                            Go to Subscriptions
                        </button>
                    ) : (
                        <button
                            onClick={() => window.close()}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Don't render puzzle view until name is set (for guests)
    // This is a safety check - the dialog should have already handled this
    // BUT: Skip this check if user has teacher role (via URL or cached)
    if (!isAuthenticated && (!finalUserName || !finalUserName.trim()) && urlRole !== 'teacher' && cachedSessionRole !== 'teacher') {
        // If we get here, something went wrong - show dialog again
        if (!showNameDialog) {
            setShowNameDialog(true);
        }
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Please enter your name to continue...</p>
                </div>
            </div>
        );
    }

    // Determine user role with priority:
    // 1. Existing participant role (for page refreshes)
    // 2. SessionStorage cached role for this session
    // 3. URL parameter (explicit role specification)
    // 4. Authentication and activeRole
    let userRole: 'teacher' | 'student';
    const existingParticipant = session?.participants?.[finalUserId];
    // sessionRoleKey already declared at top, reuse it
    const cachedRole = cachedSessionRole; // Use the one declared earlier
    
    console.log('🔍 Checking for existing participant:', {
        finalUserId,
        urlRole,
        cachedRole,
        existingParticipant: existingParticipant ? {
            userId: existingParticipant.userId,
            userName: existingParticipant.userName,
            userRole: existingParticipant.userRole,
            isActive: existingParticipant.isActive
        } : null,
        allParticipants: session?.participants ? Object.entries(session.participants).map(([id, p]) => ({
            id,
            name: p.userName,
            role: p.userRole
        })) : []
    });
    
    if (existingParticipant) {
        // Use existing role from session (HIGHEST PRIORITY - preserves role on refresh)
        userRole = existingParticipant.userRole;
        console.log('✅ Found existing participant, using their stored role:', userRole);
    } else if (cachedRole) {
        // Use cached role from previous visit to this session (SECOND PRIORITY)
        userRole = cachedRole;
        console.log('✅ Using cached role from sessionStorage:', userRole);
    } else if (urlRole) {
        // URL parameter specified role (for new participants)
        userRole = urlRole;
        console.log('🆕 New participant, using URL role:', urlRole);
    } else {
        // Determine role based on authentication
        userRole = isAuthenticated
            ? ((activeRole?.role === 'ServiceProvider' || activeRole?.role === 'Admin') ? 'teacher' : 'student')
            : 'student';
        console.log('🆕 New participant, determined role:', userRole, 'based on activeRole:', activeRole?.role);
    }
    
    console.log('🎭 Final user role determination:', {
        isAuthenticated,
        activeRole: activeRole?.role,
        urlRole,
        finalUserRole: userRole,
        userId: finalUserId,
        userName: finalUserName,
        existingParticipant: !!existingParticipant
    });

    // Render collaborative puzzle view
    return (
        <div className="h-screen w-screen overflow-hidden">
            <CollaborativePuzzleView
                sessionId={sessionId!}
                subscriptionId={subscriptionId}
                puzzle={session.puzzle}
                userId={finalUserId}
                userName={finalUserName}
                userRole={userRole}
                onClose={() => {
                    if (isAuthenticated) {
                        navigate('/mysubscriptions');
                    } else {
                        // For guests, just close the tab or show a message
                        window.close();
                    }
                }}
            />
        </div>
    );
};

export default CollaborativeSessionPage;
